import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Add, KeyboardArrowDown, Search } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { getLibraryPageTokens } from '../getLibraryPageTokens';
import { LIBRARY_SORT_OPTIONS, type LibrarySortOrder } from '../libraryStatus';
import { LIBRARY_PAGE_LAYOUT_SIZES } from '../libraryTokens';

export interface LibraryFilterToolbarProps {
  onNewExperiment: () => void;
  onSearchChange: (value: string) => void;
  onSortChange: (value: LibrarySortOrder) => void;
  searchQuery: string;
  sortOrder: LibrarySortOrder;
}

export function LibraryFilterToolbar({
  onNewExperiment,
  onSearchChange,
  onSortChange,
  searchQuery,
  sortOrder,
}: LibraryFilterToolbarProps): JSX.Element {
  const theme = useTheme();
  const tokens = getLibraryPageTokens(theme);

  const controlSx = {
    height: `${LIBRARY_PAGE_LAYOUT_SIZES.toolbarControlHeight}px`,
    borderRadius: `${LIBRARY_PAGE_LAYOUT_SIZES.toolbarFieldRadius}px`,
    bgcolor: tokens.toolbarFieldBg,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  };

  function handleSortChange(event: SelectChangeEvent<LibrarySortOrder>): void {
    onSortChange(event.target.value as LibrarySortOrder);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: `${LIBRARY_PAGE_LAYOUT_SIZES.toolbarGap}px`,
      }}
    >
      <Select
        size="small"
        value={sortOrder}
        onChange={handleSortChange}
        IconComponent={KeyboardArrowDown}
        sx={{
          ...controlSx,
          minWidth: 112,
          color: tokens.filterLabelColor,
          fontSize: '16px',
          fontWeight: 500,
          '& .MuiSelect-icon': { color: tokens.filterLabelColor },
        }}
      >
        {LIBRARY_SORT_OPTIONS.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      <TextField
        size="small"
        placeholder="Search..."
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: tokens.pageSubtitleColor }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          ...controlSx,
          flex: '1 1 200px',
          maxWidth: 280,
          '& .MuiInputBase-input': {
            fontSize: '16px',
            color: tokens.filterLabelColor,
          },
        }}
      />

      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={onNewExperiment}
        sx={{
          ml: { md: 'auto' },
          minHeight: `${LIBRARY_PAGE_LAYOUT_SIZES.toolbarControlHeight}px`,
          height: `${LIBRARY_PAGE_LAYOUT_SIZES.toolbarControlHeight}px`,
          px: 2,
          borderRadius: `${LIBRARY_PAGE_LAYOUT_SIZES.toolbarFieldRadius}px`,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '16px',
          bgcolor: tokens.primaryButtonBg,
          color: tokens.primaryButtonColor,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: tokens.primaryButtonHoverBg,
            boxShadow: 'none',
          },
        }}
      >
        New Experiment
      </Button>
    </Box>
  );
}
